import { buttonVariants } from "./button";
import { amiri } from "@/app/fonts";
import { QuranWord } from "./quran-word";
import { QuranResponseType } from "@/lib/types";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import useIntersectionObserver from "@/lib/hooks/useIntersectionObserver";
import { debounce } from "@/lib/helpers/functions";
import {
  useQuran,
  useQuranOptions,
} from "@/app/providers/QuranContextProvider";
import QuranTranslations from "./quran-translations";
import QuranAyahNote from "./quran-ayah-note";
import QuranAyahCopy from "./quran-ayah-copy";
import { Card, CardContent } from "./card";
import { cn } from "@/lib/helpers/utils";
import QuranAyahLike from "./quran-ayah-like";
import QuranAyahShowTafsir from "./quran-ayah-tafsir";
import QuranAyahStartRecitation from "./quran-ayah-play";
import QuranAyahShare from "./quran-ayah-share";
import ToolTipNavItem from "./tooltip-nav-item";

const QuranTranslationsMemoized = memo(QuranTranslations);
export const QuranAyahMemoized = memo(QuranAyah);
export default function QuranAyah({
  data,
  shouldTrack = true,
}: {
  data: QuranResponseType;
  shouldTrack?: boolean;
}) {
  const { quranMushaf, quranFont, showQuarnTranslation, quranFontSize } =
    useQuranOptions();
  const sidedivs = useMemo(() => {
    return (
      <div className="flex flex-row md:flex-row">
        <QuranAyahStartRecitation />
        <ToolTipNavItem direction="top" tooltipText="Notes and Reflections">
          <QuranAyahNote id={data.id} surah={data.surah} verse={data.verse}>
            <div
              className={`${buttonVariants({
                variant: "bgGhost",
              })} h-9 w-9 p-0 cursor-pointer`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className=" w-5 h-5"
              >
                <path d="M8 2v4"></path>
                <path d="M12 2v4"></path>
                <path d="M16 2v4"></path>
                <rect width="16" height="18" x="4" y="4" rx="2"></rect>
                <path d="M8 10h6"></path>
                <path d="M8 14h8"></path>
                <path d="M8 18h5"></path>
              </svg>
            </div>
          </QuranAyahNote>
        </ToolTipNavItem>
        <QuranAyahShowTafsir />
        <QuranAyahLike id={data.id} />
        <QuranAyahCopy ayah={data} />
        <QuranAyahShare />
      </div>
    );
  }, []);
  const words = useMemo(() => {
    const ayah = data;
    return (
      <>
        {ayah.words?.map((word) => (
          <QuranWord
            key={`${word.surah}:${word.verse}:${word.word}`}
            word={word}
            preview={false}
          />
        ))}
      </>
    );
  }, [data, quranMushaf]);

  // console.log("data QuranAyah: ", data);
  return (
    <div id={`s:${data.surah}-a${data.verse}`}>
      {/* {surah === ayah.surah && ( */}
      {shouldTrack && (
        <QuranViewTracker
          page={data.page}
          surah={data.surah}
          verse={data.verse}
          juz={data.juz}
          ruku={data.ruku}
          id={data.id}
        />
      )}
      <Card className="flex flex-col mb-3 ">
        <div className="flex bg-primary/10 flex-row justify-between">
          <div className="p-2 w-9 text-sm font-bold text-center text-text">
            {data.verse}
          </div>
          {sidedivs}
        </div>
        <CardContent className="pt-7 md:pt-0 w-full">
          <div>
            {/* {data.verse != 1 && <Separator className="mt-5" />} */}
            <div className="flex flex-col md:flex-row gap-5 w-full mt-0 md:mt-5">
              <div className="right w-full flex flex-col justify-between">
                <div className="flex flex-row justify-end">
                  <div
                    dir="rtl"
                    className={cn(
                      `${amiri.className} flex flex-wrap text-right mb-3 cursor-default leading-loose`,
                      // `${amiri.className} flex flex-wrap text-right mb-3 cursor-default text-[1.5rem] leading-loose`,
                      // quranFont == "hafs1" ? "text-[2.0rem]" : "",
                      // quranFont == "indo-pak" ? "text-[1.7rem]" : "",
                    )}
                    style={{
                      fontSize: `${quranFont == "hafs1" ? quranFontSize + 0.5 : quranFont == "indo-pak" ? quranFontSize + 0.2 : quranFontSize}rem`,
                    }}
                  >
                    {words}
                  </div>
                </div>
                <div className="flex flex-col justify-between">
                  {/* <div className="mb-5"> */}
                  {/*   <div className="text-xs font-bold text-primary/80 pb-1"> */}
                  {/*     Transliteration */}
                  {/*   </div> */}
                  {/*   {/* Transliteration */}
                  {/*   <div className="text-text"> */}
                  {/*     In the Name of Allah—the Most Compassionate, Most */}
                  {/*     Merciful. */}
                  {/*   </div> */}
                  {/* </div> */}
                  {showQuarnTranslation && (
                    <QuranTranslationsMemoized
                      surah={data.surah}
                      verse={data.verse}
                      page={data.page}
                      verseId={data.id as number}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

const QuranViewTracker = ({
  page,
  verse,
  surah,
  ruku,
  juz,
  id,
}: {
  page: number;
  surah: number;
  verse: number;
  ruku: number;
  juz: number;
  id: number | string;
}) => {
  const { activeViewRef } = useQuran();
  const emitCurActiveViewEvent = useCallback(() => {
    const eventDetail = {
      page,
      verse,
      surah,
      ruku,
      juz,
      manzil: 1,
      id,
    };

    activeViewRef.current = {
      ...eventDetail,
      id: Number.parseInt(id.toString()),
    };

    const event = new CustomEvent("curActiveView", {
      detail: eventDetail,
    });

    window.dispatchEvent(event);
  }, [, page, verse, juz, ruku, surah, activeViewRef]);

  const debounced = debounce(emitCurActiveViewEvent, 15);
  const [ref] = useIntersectionObserver(
    {
      root: null,
      rootMargin: "0px 0px -80% 0px",
      threshold: 0,
      observeFirstElement: true,
    },
    debounced,
  );

  // useEffect(() => {
  //   if (isIntersecting) {
  //     debounced();
  //     // emitCurActiveViewEvent();
  //   }
  // }, [
  //   isIntersecting,
  //   emitCurActiveViewEvent,
  //   debounced,
  //   page,
  //   verse,
  //   surah,
  //   ruku,
  //   juz,
  // ]);

  return <div ref={ref}></div>;
};
